import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import type {
  CatalogEntryView,
  CatalogLookupResult,
  CatalogSuggestions,
  CursorPage,
  SetCatalogEntryHiddenInput,
  UpdateCatalogEntryInput,
} from "../../shared/contracts.js";
import { CATALOG_SUGGEST_MIN_PREFIX } from "../../shared/contracts.js";
import { normalizePartNumber } from "../../shared/formatting.js";
import type { InfinitePages } from "./caseQueries.js";
import { request } from "./http.js";

/**
 * The parts catalog (spec §6) as the client reads and edits it.
 *
 * The order forms ask two questions while Parts types a part number: "do
 * you know this exact number?" (`lookup`, on leaving the field) and "what
 * starts like this?" (`suggest`, from the third character, debounced so a
 * fast typist does not fire a request per keystroke). The catalog page
 * lists, searches, edits descriptions and hides entries. Everything hangs
 * under the `["catalog", …]` root, invalidated after every edit; the
 * lookups the forms cache are short-lived, so a description just changed
 * on the catalog page reaches the next order without a reload.
 */

/** How long the forms wait after the last keystroke before asking for suggestions. */
export const SUGGEST_DEBOUNCE_MS = 200;

export type CatalogListParams = Readonly<{
  q?: string;
  includeHidden?: boolean;
}>;

export const catalogQueryKeys = {
  root: ["catalog"] as const,
  lookup: (normalizedPartNumber: string) => ["catalog", "lookup", normalizedPartNumber] as const,
  suggest: (normalizedPrefix: string) => ["catalog", "suggest", normalizedPrefix] as const,
  list: (params: Required<CatalogListParams>) => ["catalog", "list", params] as const,
};

export async function fetchCatalogLookup(partNumber: string): Promise<CatalogEntryView | null> {
  const search = new URLSearchParams({ partNumber });
  const result = await request<CatalogLookupResult>(`/api/catalog/lookup?${search.toString()}`);
  return result.entry;
}

export async function fetchCatalogSuggestions(prefix: string): Promise<readonly CatalogEntryView[]> {
  const search = new URLSearchParams({ prefix });
  const result = await request<CatalogSuggestions>(`/api/catalog/suggest?${search.toString()}`);
  return result.items;
}

export async function fetchCatalogPage(
  params: Required<CatalogListParams>,
  cursor: string | null,
): Promise<CursorPage<CatalogEntryView>> {
  const search = new URLSearchParams();
  if (params.q !== "") search.set("q", params.q);
  if (params.includeHidden) search.set("includeHidden", "true");
  if (cursor !== null) search.set("cursor", cursor);
  const query = search.toString();
  return request<CursorPage<CatalogEntryView>>(`/api/catalog${query === "" ? "" : `?${query}`}`);
}

/**
 * An on-demand exact lookup for the order forms: `lookup(partNumber)`
 * resolves to the entry or `null`, through the query cache (30 s), so
 * leaving and re-entering the same field does not ask twice. A number
 * with no letter or digit is never sent.
 */
export function useCatalogLookup(): (partNumber: string) => Promise<CatalogEntryView | null> {
  const queryClient = useQueryClient();
  return useCallback(
    async (partNumber: string) => {
      const typed = partNumber.trim();
      const key = normalizePartNumber(typed);
      if (key.length === 0) return null;
      return queryClient.fetchQuery({
        queryKey: catalogQueryKeys.lookup(key),
        queryFn: () => fetchCatalogLookup(typed),
        staleTime: 30_000,
      });
    },
    [queryClient],
  );
}

/** `value` as it stood `delay` ms after it last changed. */
function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/**
 * Suggestions for what is typed in a part-number field: nothing until the
 * typed text is at least three characters, then the server's matches by
 * normalized prefix, 200 ms after the last keystroke. `enabled: false`
 * (a closed list, a locked line) asks nothing.
 */
export function useCatalogSuggest(
  typed: string,
  options: { enabled?: boolean } = {},
): UseQueryResult<readonly CatalogEntryView[]> {
  const trimmed = typed.trim();
  const debounced = useDebounced(trimmed, SUGGEST_DEBOUNCE_MS);
  const ready = isSuggestPrefix(debounced);
  const normalized = normalizePartNumber(debounced);
  return useQuery({
    queryKey: catalogQueryKeys.suggest(normalized),
    queryFn: () => fetchCatalogSuggestions(debounced),
    enabled: (options.enabled ?? true) && ready,
    staleTime: 30_000,
    // While the next keystroke's answer is on its way, keep showing the last
    // one only when the new text extends it (its matches are a superset of
    // what is coming). Backspacing or retyping shows nothing until the server
    // answers; a prefix too short to ask about never borrows an earlier list.
    placeholderData: (previous, previousQuery) =>
      ready && previousQuery !== undefined && normalized.startsWith(String(previousQuery.queryKey[2] ?? ""))
        ? previous
        : undefined,
  });
}

/** Whether typed text is long enough to ask for suggestions (spec §6.2: from the third character). */
export function isSuggestPrefix(typed: string): boolean {
  const trimmed = typed.trim();
  return trimmed.length >= CATALOG_SUGGEST_MIN_PREFIX && normalizePartNumber(trimmed).length > 0;
}

/** The catalog page's list: one infinite query per search + hidden filter. */
export function useCatalogList(
  params: CatalogListParams,
): UseInfiniteQueryResult<InfinitePages<CatalogEntryView>> {
  const normalized: Required<CatalogListParams> = {
    q: (params.q ?? "").trim(),
    includeHidden: params.includeHidden ?? false,
  };
  return useInfiniteQuery({
    queryKey: catalogQueryKeys.list(normalized),
    queryFn: ({ pageParam }) => fetchCatalogPage(normalized, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });
}

export function updateCatalogEntry(
  normalizedPartNumber: string,
  input: UpdateCatalogEntryInput,
): Promise<CatalogEntryView> {
  return request<CatalogEntryView>(`/api/catalog/${encodeURIComponent(normalizedPartNumber)}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export function setCatalogEntryHidden(
  normalizedPartNumber: string,
  input: SetCatalogEntryHiddenInput,
): Promise<CatalogEntryView> {
  return request<CatalogEntryView>(
    `/api/catalog/${encodeURIComponent(normalizedPartNumber)}/hidden`,
    { method: "POST", body: JSON.stringify(input) },
  );
}

/** Edits a description; every catalog query is refetched afterwards, the forms' lookups included. */
export function useUpdateCatalogEntry(): UseMutationResult<
  CatalogEntryView,
  Error,
  { normalizedPartNumber: string; input: UpdateCatalogEntryInput }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ normalizedPartNumber, input }) => updateCatalogEntry(normalizedPartNumber, input),
    onSettled: () => queryClient.invalidateQueries({ queryKey: catalogQueryKeys.root }),
  });
}

export function useSetCatalogEntryHidden(): UseMutationResult<
  CatalogEntryView,
  Error,
  { normalizedPartNumber: string; hidden: boolean }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ normalizedPartNumber, hidden }) => setCatalogEntryHidden(normalizedPartNumber, { hidden }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: catalogQueryKeys.root }),
  });
}
